import React, { useState } from 'react';
import toast from 'react-hot-toast';

import {
  fetchSharePointDocumentDiff,
  fetchSharePointDocumentHistory,
  fetchSharePointDocumentRevisions,
  resolveSharePointDocumentUrl,
  updateSharePointDocumentVersioned,
} from '../../services/api';

const emptyForm = {
  document_url: '',
  drive_id: '',
  item_id: '',
  content:
    '<h1>Supermanager SharePoint Test</h1><p>This document was updated from the frontend consumer test page.</p>',
  comment: 'Supermanager frontend test update',
  app_name: '',
  graph_access_token: '',
  publish_as_major: true,
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const JsonBlock = ({ data }) => (
  <pre className="max-h-96 overflow-auto rounded-2xl bg-slate-950 p-4 text-xs leading-relaxed text-slate-100 shadow-inner">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const DiffView = ({ segments }) => (
  <div className="max-h-96 overflow-auto whitespace-pre-wrap rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-800">
    {segments.map((seg, index) => {
      if (seg.op === 'insert') {
        return (
          <ins
            key={index}
            className="rounded bg-emerald-100 text-emerald-900 no-underline"
          >
            {seg.text}
          </ins>
        );
      }
      if (seg.op === 'delete') {
        return (
          <del key={index} className="rounded bg-rose-100 text-rose-900">
            {seg.text}
          </del>
        );
      }
      return <span key={index}>{seg.text}</span>;
    })}
  </div>
);

const SharePointDocumentTester = ({ userIdToken }) => {
  const [form, setForm] = useState(emptyForm);
  const [updateResult, setUpdateResult] = useState(null);
  const [historyResult, setHistoryResult] = useState(null);
  const [resolveResult, setResolveResult] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  const [revisions, setRevisions] = useState([]);
  const [isLoadingRevisions, setIsLoadingRevisions] = useState(false);
  const [fromVersionId, setFromVersionId] = useState('');
  const [toVersionId, setToVersionId] = useState('');
  const [diffResult, setDiffResult] = useState(null);
  const [isLoadingDiff, setIsLoadingDiff] = useState(false);

  // Use the project token (carries `project_id` the backend verifies); the user
  // token returns 401 and triggers a force-logout in api.js.
  const token = localStorage.getItem('projectToken') || userIdToken;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const buildPayload = () => ({
    drive_id: form.drive_id.trim() || null,
    item_id: form.item_id.trim(),
    content: form.content,
    comment: form.comment.trim(),
    publish_as_major: form.publish_as_major,
    app_name: form.app_name.trim() || null,
    access_token: form.graph_access_token.trim() || null,
  });

  const validateForm = () => {
    if (!token) {
      toast.error('Login token missing. Please login again.');
      return false;
    }
    if (!form.item_id.trim()) {
      toast.error('Item ID is required.');
      return false;
    }
    if (!form.content.trim()) {
      toast.error('Document HTML content is required.');
      return false;
    }
    if (!form.comment.trim()) {
      toast.error('Check-in comment is required.');
      return false;
    }
    return true;
  };

  const handleResolveUrl = async () => {
    if (!token) {
      toast.error('Login token missing. Please login again.');
      return;
    }
    if (!form.document_url.trim()) {
      toast.error('Paste a SharePoint document URL first.');
      return;
    }

    setIsResolving(true);
    setResolveResult(null);
    try {
      const result = await resolveSharePointDocumentUrl(token, {
        document_url: form.document_url.trim(),
        app_name: form.app_name.trim() || null,
        access_token: form.graph_access_token.trim() || null,
      });
      setResolveResult(result);
      setForm((current) => ({
        ...current,
        drive_id: result.drive_id || current.drive_id,
        item_id: result.item_id || current.item_id,
      }));
      toast.success('Document URL resolved.');
    } catch (error) {
      setResolveResult({ error });
      toast.error(typeof error === 'string' ? error : 'URL resolve failed.');
    } finally {
      setIsResolving(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsUpdating(true);
    setUpdateResult(null);
    try {
      const result = await updateSharePointDocumentVersioned(
        token,
        buildPayload(),
      );
      setUpdateResult(result);
      toast.success('Document updated and checked in.');
      await loadHistory();
    } catch (error) {
      setUpdateResult({ error });
      toast.error(typeof error === 'string' ? error : 'Update failed.');
    } finally {
      setIsUpdating(false);
    }
  };

  const loadHistory = async () => {
    if (!token) {
      toast.error('Login token missing. Please login again.');
      return;
    }
    if (!form.item_id.trim()) {
      toast.error('Item ID is required to load history.');
      return;
    }

    setIsLoadingHistory(true);
    try {
      const result = await fetchSharePointDocumentHistory(token, {
        drive_id: form.drive_id.trim() || undefined,
        item_id: form.item_id.trim(),
        limit: 20,
      });
      setHistoryResult(result);
    } catch (error) {
      setHistoryResult({ error });
      toast.error(typeof error === 'string' ? error : 'History load failed.');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadRevisions = async () => {
    if (!token) {
      toast.error('Login token missing. Please login again.');
      return;
    }
    if (!form.item_id.trim()) {
      toast.error('Item ID is required to load revisions.');
      return;
    }

    setIsLoadingRevisions(true);
    try {
      const result = await fetchSharePointDocumentRevisions(token, {
        item_id: form.item_id.trim(),
        drive_id: form.drive_id.trim() || undefined,
        app_name: form.app_name.trim() || undefined,
        access_token: form.graph_access_token.trim() || undefined,
      });
      const list = result?.revisions || [];
      setRevisions(list);
      // Default the diff selectors to the two newest revisions.
      if (list.length >= 2) {
        setToVersionId(list[0].version_id);
        setFromVersionId(list[1].version_id);
      } else if (list.length === 1) {
        setToVersionId(list[0].version_id);
        setFromVersionId(list[0].version_id);
      }
    } catch (error) {
      setRevisions([]);
      toast.error(typeof error === 'string' ? error : 'Revisions load failed.');
    } finally {
      setIsLoadingRevisions(false);
    }
  };

  const loadDiff = async () => {
    if (!token) {
      toast.error('Login token missing. Please login again.');
      return;
    }
    if (!form.item_id.trim()) {
      toast.error('Item ID is required to compare versions.');
      return;
    }
    if (!fromVersionId || !toVersionId) {
      toast.error('Select two versions to compare.');
      return;
    }

    setIsLoadingDiff(true);
    setDiffResult(null);
    try {
      const result = await fetchSharePointDocumentDiff(token, {
        item_id: form.item_id.trim(),
        from_version_id: fromVersionId,
        to_version_id: toVersionId,
        drive_id: form.drive_id.trim() || undefined,
        app_name: form.app_name.trim() || undefined,
        access_token: form.graph_access_token.trim() || undefined,
      });
      setDiffResult(result);
    } catch (error) {
      setDiffResult({ error });
      toast.error(typeof error === 'string' ? error : 'Diff load failed.');
    } finally {
      setIsLoadingDiff(false);
    }
  };

  const latestVersion = updateResult?.latest_version;

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,#dbeafe,transparent_34%),linear-gradient(135deg,#f8fafc_0%,#ecfeff_55%,#fff7ed_100%)] px-6 py-8 text-slate-950">
      <div className="mx-auto max-w-7xl">
        <section className="mb-8 rounded-[2rem] border border-white/70 bg-white/75 p-8 shadow-2xl shadow-slate-200/70 backdrop-blur">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-700">
            SharePoint Consumer Test
          </p>
          <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
            Update a SharePoint document from the frontend
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">
            Use this page like a consumer flow: enter the SharePoint document
            identifiers, update the full document content, add a check-in
            comment, then inspect SharePoint version metadata and local
            Supermanager audit history.
          </p>
        </section>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <form
            onSubmit={handleUpdate}
            className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-xl shadow-slate-200/70"
          >
            <label className="mb-4 block">
              <span className="text-sm font-bold text-slate-700">
                SharePoint Document URL
              </span>
              <div className="mt-2 flex flex-col gap-2 md:flex-row">
                <input
                  name="document_url"
                  value={form.document_url}
                  onChange={handleChange}
                  placeholder="Paste the SharePoint file URL here"
                  className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-cyan-500 focus:bg-white focus:ring-4 focus:ring-cyan-100"
                />
                <button
                  type="button"
                  onClick={handleResolveUrl}
                  disabled={isResolving}
                  className="rounded-2xl bg-cyan-700 px-5 py-3 text-sm font-black text-white shadow-lg shadow-cyan-200 transition hover:-translate-y-0.5 hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isResolving ? 'Resolving...' : 'Resolve URL'}
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Use this first for consumer testing. It fills Drive ID and Item
                ID automatically when Microsoft Graph can resolve the URL.
              </p>
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Drive ID
                </span>
                <input
                  name="drive_id"
                  value={form.drive_id}
                  onChange={handleChange}
                  placeholder="SharePoint document library drive ID"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-cyan-500 focus:bg-white focus:ring-4 focus:ring-cyan-100"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Item ID
                </span>
                <input
                  name="item_id"
                  value={form.item_id}
                  onChange={handleChange}
                  placeholder="SharePoint file item ID"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-cyan-500 focus:bg-white focus:ring-4 focus:ring-cyan-100"
                  required
                />
              </label>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Credential App
                </span>
                <select
                  name="app_name"
                  value={form.app_name}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-cyan-500 focus:bg-white focus:ring-4 focus:ring-cyan-100"
                >
                  <option value="">Auto-detect Microsoft credential</option>
                  <option value="microsoft_word">Microsoft Word</option>
                  <option value="microsoft_outlook">Microsoft Outlook</option>
                  <option value="microsoft_teams">Microsoft Teams</option>
                </select>
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Manual Graph Token
                </span>
                <input
                  name="graph_access_token"
                  value={form.graph_access_token}
                  onChange={handleChange}
                  placeholder="Optional dev fallback"
                  type="password"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-cyan-500 focus:bg-white focus:ring-4 focus:ring-cyan-100"
                />
              </label>
            </div>

            <label className="mt-4 block">
              <span className="text-sm font-bold text-slate-700">
                Check-in Comment
              </span>
              <input
                name="comment"
                value={form.comment}
                onChange={handleChange}
                placeholder="Shown in SharePoint version history"
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-cyan-500 focus:bg-white focus:ring-4 focus:ring-cyan-100"
                required
              />
            </label>

            <label className="mt-4 block">
              <span className="text-sm font-bold text-slate-700">
                Full Document HTML
              </span>
              <textarea
                name="content"
                value={form.content}
                onChange={handleChange}
                rows={12}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-950 px-4 py-3 font-mono text-sm leading-6 text-slate-100 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100"
                required
              />
            </label>

            <label className="mt-4 flex items-center gap-3 rounded-2xl bg-cyan-50 px-4 py-3 text-sm font-semibold text-cyan-900">
              <input
                name="publish_as_major"
                type="checkbox"
                checked={form.publish_as_major}
                onChange={handleChange}
                className="h-4 w-4 accent-cyan-700"
              />
              Publish/check in as major version when supported
            </label>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={isUpdating}
                className="rounded-2xl bg-slate-950 px-6 py-3 text-sm font-black text-white shadow-lg shadow-slate-300 transition hover:-translate-y-0.5 hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isUpdating ? 'Updating...' : 'Update Document'}
              </button>
              <button
                type="button"
                onClick={loadHistory}
                disabled={isLoadingHistory}
                className="rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-black text-slate-900 transition hover:-translate-y-0.5 hover:border-cyan-500 hover:text-cyan-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoadingHistory ? 'Loading...' : 'Load History'}
              </button>
              <button
                type="button"
                onClick={loadRevisions}
                disabled={isLoadingRevisions}
                className="rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-black text-slate-900 transition hover:-translate-y-0.5 hover:border-cyan-500 hover:text-cyan-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoadingRevisions ? 'Loading...' : 'Load Revisions'}
              </button>
            </div>
          </form>

          <aside className="space-y-6">
            {resolveResult && (
              <section className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-xl shadow-slate-200/70">
                <h2 className="text-xl font-black text-slate-950">
                  URL Resolution
                </h2>
                <div className="mt-4 space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl bg-cyan-50 p-4">
                      <p className="text-xs font-bold uppercase text-cyan-700">
                        Drive ID
                      </p>
                      <p className="mt-1 break-all text-xs font-black text-cyan-950">
                        {resolveResult.drive_id || 'N/A'}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-cyan-50 p-4">
                      <p className="text-xs font-bold uppercase text-cyan-700">
                        Item ID
                      </p>
                      <p className="mt-1 break-all text-xs font-black text-cyan-950">
                        {resolveResult.item_id || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <JsonBlock data={resolveResult} />
                </div>
              </section>
            )}

            <section className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-xl shadow-slate-200/70">
              <h2 className="text-xl font-black text-slate-950">
                Latest Update
              </h2>
              {updateResult ? (
                <div className="mt-4 space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <p className="text-xs font-bold uppercase text-slate-500">
                        Version
                      </p>
                      <p className="mt-1 text-lg font-black text-slate-950">
                        {latestVersion?.id || 'N/A'}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <p className="text-xs font-bold uppercase text-slate-500">
                        Credential
                      </p>
                      <p className="mt-1 text-sm font-black text-slate-950">
                        {updateResult.credential_source || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <JsonBlock data={updateResult} />
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">
                  Submit an update to see the SharePoint response and audit log.
                </p>
              )}
            </section>

            <section className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-xl shadow-slate-200/70">
              <h2 className="text-xl font-black text-slate-950">
                Version History
              </h2>
              {revisions.length ? (
                <div className="mt-4 space-y-4">
                  <ol className="space-y-2">
                    {revisions.map((rev) => (
                      <li
                        key={rev.version_id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-black text-slate-950">
                            {rev.author?.display_name || 'Unknown author'}
                          </p>
                          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                            v{rev.version_id}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {formatDate(rev.last_modified)}
                        </p>
                        {rev.comment ? (
                          <p className="mt-2 text-xs italic text-slate-600">
                            “{rev.comment}”
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ol>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-xs font-bold uppercase text-slate-500">
                        Compare from
                      </span>
                      <select
                        value={fromVersionId}
                        onChange={(e) => setFromVersionId(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-cyan-500 focus:bg-white"
                      >
                        {revisions.map((rev) => (
                          <option key={rev.version_id} value={rev.version_id}>
                            v{rev.version_id} ·{' '}
                            {rev.author?.display_name || 'Unknown'}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-bold uppercase text-slate-500">
                        Compare to
                      </span>
                      <select
                        value={toVersionId}
                        onChange={(e) => setToVersionId(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-cyan-500 focus:bg-white"
                      >
                        {revisions.map((rev) => (
                          <option key={rev.version_id} value={rev.version_id}>
                            v{rev.version_id} ·{' '}
                            {rev.author?.display_name || 'Unknown'}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={loadDiff}
                    disabled={isLoadingDiff}
                    className="w-full rounded-2xl bg-slate-950 px-6 py-3 text-sm font-black text-white shadow-lg shadow-slate-300 transition hover:-translate-y-0.5 hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isLoadingDiff ? 'Comparing...' : 'Show Changes'}
                  </button>

                  {diffResult?.segments ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-500">
                        <span className="text-emerald-700">
                          +{diffResult.summary?.chars_added ?? 0} added
                        </span>{' '}
                        ·{' '}
                        <span className="text-rose-700">
                          -{diffResult.summary?.chars_removed ?? 0} removed
                        </span>
                      </p>
                      <DiffView segments={diffResult.segments} />
                    </div>
                  ) : diffResult?.error ? (
                    <JsonBlock data={diffResult} />
                  ) : null}
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">
                  Load revisions to see who changed what, then compare two
                  versions.
                </p>
              )}
            </section>

            <section className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-xl shadow-slate-200/70">
              <h2 className="text-xl font-black text-slate-950">
                Supermanager History
              </h2>
              {historyResult?.records?.length ? (
                <div className="mt-4 space-y-3">
                  {historyResult.records.map((record) => (
                    <article
                      key={record._id}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <p className="text-sm font-black text-slate-950">
                        {record.checkin_comment}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {formatDate(record.created_at)} · Version{' '}
                        {record.latest_version?.id || 'N/A'}
                      </p>
                      <p className="mt-2 break-all text-xs text-slate-500">
                        {record.document_name || record.item_id}
                      </p>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">
                  No local audit records loaded yet.
                </p>
              )}
            </section>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default SharePointDocumentTester;
